import { dirhams, type MonthKey, monthLabel, toCents } from '@horof/shared';

type MonthTotals = { month: string; income: string; expenses: string };

const SLOT = 60;
const HEIGHT = 170;
const BAR = 20;

/**
 * Income (green) and expenses (red) for the 12 months of a year. Months run
 * right to left, as Arabic reads. A click on a month opens its summary.
 */
export function MonthBars({
  months,
  selected,
  onPick,
}: {
  months: MonthTotals[];
  selected?: string | null;
  onPick?: (month: MonthKey) => void;
}) {
  const max = Math.max(
    1,
    ...months.flatMap((m) => [toCents(m.income), toCents(m.expenses)]),
  );
  const h = (amount: string) => Math.round((toCents(amount) / max) * HEIGHT);
  const width = SLOT * months.length;

  return (
    <svg
      className="desk-bars"
      viewBox={`0 0 ${width} ${HEIGHT + 30}`}
      role="img"
      aria-label="المداخيل والمصاريف حسب الشهر"
    >
      {months.map((m, i) => {
        const x = width - (i + 1) * SLOT;
        const label = monthLabel(m.month as MonthKey).split(' ')[0];
        const income = h(m.income);
        const expenses = h(m.expenses);
        return (
          // biome-ignore lint/a11y/noStaticElementInteractions: the summary is also in the menu
          <g
            key={m.month}
            className={`desk-bar${m.month === selected ? ' is-selected' : ''}${onPick ? ' is-clickable' : ''}`}
            onClick={() => onPick?.(m.month as MonthKey)}
          >
            <title>
              {`${monthLabel(m.month as MonthKey)}: المداخيل ${dirhams(m.income)} · المصاريف ${dirhams(m.expenses)}`}
            </title>
            <rect
              x={x}
              y={0}
              width={SLOT}
              height={HEIGHT + 30}
              className="desk-bar-slot"
            />
            <rect
              x={x + SLOT / 2}
              y={HEIGHT - income}
              width={BAR}
              height={income}
              className="is-credit"
            />
            <rect
              x={x + SLOT / 2 - BAR}
              y={HEIGHT - expenses}
              width={BAR}
              height={expenses}
              className="is-debit"
            />
            <text x={x + SLOT / 2} y={HEIGHT + 20} textAnchor="middle">
              {label}
            </text>
          </g>
        );
      })}
      <line
        x1={0}
        x2={width}
        y1={HEIGHT}
        y2={HEIGHT}
        className="desk-bars-axis"
      />
    </svg>
  );
}

/** A thin bar showing a share (0–100) of a total. */
export function ShareBar({
  percent,
  tone,
}: {
  percent: number;
  tone: 'credit' | 'debit' | 'neutral';
}) {
  return (
    <span className="desk-share">
      <span
        className={`desk-share-fill is-${tone}`}
        style={{ width: `${Math.min(100, Math.max(0, percent))}%` }}
      />
    </span>
  );
}
